// UI strings for the translator area, Finnish (default) and English side by side.
// Finnish is written in full and never truncated; en must have exactly the same shape.
// Functions that take an HV word return parts (string | the word) so the screen can set the word
// in the HV face inside the sentence.
import type { TranslateDirection } from '@valyrio/engine/types';
import type { ReasonKey } from '../../translator/confidence';
import { noteStrings } from '../../translator/notes';
import { defineArea } from '../area';

type Parts<W> = (string | W)[];
type Lang3 = 'hv' | 'fi' | 'en';

const enN = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

export const translatorFi = {
  title: 'Kääntäjä',

  // ---- direction ----
  direction: 'Käännössuunta',
  directions: {
    'hv-fi': 'valyriasta suomeksi',
    'hv-en': 'valyriasta englanniksi',
    'fi-hv': 'suomesta valyriaksi',
    'en-hv': 'englannista valyriaksi',
  } as Record<TranslateDirection, string>,
  writeIn: 'Kirjoitat',
  translateTo: 'Käännös',
  srcLang: { hv: 'valyriaa', fi: 'suomea', en: 'englantia' } as Record<Lang3, string>,
  tgtLang: { hv: 'valyriaksi', fi: 'suomeksi', en: 'englanniksi' } as Record<Lang3, string>,
  swapTo: (d: string) => `Vaihda suunta: ${d}`,
  swapMoves: 'Käännös siirtyy käännettäväksi, niin voit kääntää sen takaisin.',

  // ---- input ----
  inputLabel: {
    'hv-fi': 'Kirjoita valyriaksi',
    'hv-en': 'Kirjoita valyriaksi',
    'fi-hv': 'Kirjoita suomeksi',
    'en-hv': 'Kirjoita englanniksi',
  } as Record<TranslateDirection, string>,
  placeholder: {
    'hv-fi': 'esim. Zaldrīzes buzdari iksos daor.',
    'hv-en': 'esim. Zaldrīzes buzdari iksos daor.',
    'fi-hv': 'esim. Nainen näkee miehen.',
    'en-hv': 'esim. The woman sees the man.',
  } as Record<TranslateDirection, string>,
  translate: 'Käännä',
  clear: 'Tyhjennä',
  keysHint: (mod: string) => `${mod} + Enter kääntää heti, Esc tyhjentää.`,
  waitIndex: 'Kääntäjä tarvitsee taivutusmuotojen hakemiston, joka kootaan tälle laitteelle vain kerran. Odota hetki.',
  indexFailed: 'Taivutusmuotojen hakemistoa ei saatu valmiiksi, joten kääntäjä ei nyt toimi tällä laitteella. Lataa sivu uudelleen.',

  // ---- lists: recent, saved, lines from the shows ----
  lists: 'Aiemmat ja tallennetut',
  tabs: { recent: 'Äskeiset', saved: 'Tallennetut', show: 'Sarjasta' } as Record<'recent' | 'saved' | 'show', string>,
  recentEmpty: 'Tässä näkyvät viimeiset 30 käännöstäsi. Ne tallentuvat vain tälle laitteelle.',
  savedEmpty: 'Tallenna käännös, niin löydät sen täältä. Tallennetun lauseen voi myös harjoitella.',
  clearRecent: 'Tyhjennä äskeiset',
  remove: (s: string) => `Poista ”${s}”`,
  tryThese: 'Kokeile sarjan repliikkiä',
  noFiExamples: 'Sarjan repliikeille ei vielä ole suomennoksia, joten tässä ei ole esimerkkejä. Kirjoita oma lauseesi.',

  // ---- result ----
  result: 'Käännös',
  srResult: (s: string) => `Käännös: ${s}`,
  source: 'Lähtöteksti',
  stack: 'Sana sanalta: valyria, merkitys, käännös',
  rowHv: 'valyria',
  rowGloss: 'merkitys',
  rowFree: { fi: 'suomeksi', en: 'englanniksi' } as Record<'fi' | 'en', string>,
  tapHint: 'Napauta sanaa: näet sen tulkinnan, taivutuksen ja sanakirjatiedot.',
  unknownWord: 'tuntematon',
  foreignWord: 'ei valyriaksi',
  tokenAria: (word: string, gloss: string, label: string) => [word, gloss, label].filter(Boolean).join(' – '),

  official: 'Virallinen käännös',
  officialWiki: 'Petersonin wiki',
  officialPhrase: 'sanakirjan fraasi',
  officialBelow: 'Alla kääntäjän oma, sääntöihin perustuva käännös sana sanalta.',
  /** The users' own approved translation (resolved flag or Lausepino): one label everywhere, never "official". */
  oursMatch: 'Meidän käännös',
  oursApproved: (name: string) => `hyväksynyt ${name}`,
  // translations built on attested / approved sentences (translator/built.ts)
  builtPeterson: 'Rakennettu Petersonin lauseen pohjalta',
  builtOurs: 'Rakennettu meidän käännöksemme pohjalta',
  builtFrame: 'Pohjana oleva lause',
  builtSwaps: 'Vaihdetut sanat',
  builtHow:
    'Lauseen rakenne ja sanojen muodot ovat alkuperäisestä lauseesta. Vaihdetut sanat on taivutettu samaan muotoon omien taivutustaulukkojensa mukaan. Tarkista, että merkitys pysyy järkevänä.',
  builtFiFrame: 'Lause löytyi suomennoksista.',
  partialTitle: 'Osittain meidän käännöksistämme',
  partialHow: 'Merkityt osat ovat teidän hyväksymiänne käännöksiä. Muut osat kääntäjä teki sääntöjensä avulla.',
  partialFrom: 'hyväksytty',
  partialBuilt: 'rakennettu hyväksytyn pohjalta',

  confidence: 'Varmuus',
  levels: { high: 'Varma', medium: 'Melko varma', low: 'Epävarma' } as Record<'high' | 'medium' | 'low', string>,
  reasons: {
    allKnown: () => 'kaikki sanat tunnistettiin',
    unknown: (n: number) => (n === 1 ? '1 sana jäi tunnistamatta' : `${n} sanaa jäi tunnistamatta`),
    noFiGlosses: () => 'suomenkielisiä merkityksiä ei vielä ole',
    noVerb: () => 'verbiä ei tunnistettu',
    noFiniteVerb: () => 'lauseen ainoa verbi on infinitiivi',
    closest: () => 'osa muodoista on lähin löytynyt',
    typo: () => 'kirjoitusvirhe korjattiin arvaamalla',
    endingOnly: () => 'osa sanoista tunnistettiin vain päätteestä',
    fallbackGloss: () => 'osalta sanoista puuttuu suomenkielinen merkitys',
    unsureFi: () => 'osa suomen muodoista on epävarmoja',
    approx: () => 'yksi sija käännettiin likimain',
    names: (n: number) => (n === 1 ? 'yksi sana jätettiin nimeksi' : `${n} sanaa jätettiin nimiksi`),
    draft: () => 'suomenkielisten merkitysten joukossa on tarkistamattomia luonnoksia',
    order: () => 'sanajärjestys muutettiin',
  } as Record<ReasonKey, (n: number) => string>,
  lowAdvice: 'Pidä tulosta suuntaa antavana ja tarkista sanat sanakirjasta.',

  unknownHead: (n: number): string => (n === 1 ? 'Tunnistamaton sana' : 'Tunnistamattomat sanat'),
  foreignHead: (n: number): string => (n === 1 ? 'Sana ilman valyrian vastinetta' : 'Sanat ilman valyrian vastinetta'),
  suggestWord: 'Ehdota sanaa',
  searchDict: 'Etsi sanakirjasta',

  save: 'Tallenna',
  saved: 'Tallennettu',
  explain: 'Selitä',
  practice: 'Harjoittele tätä lausetta',
  practiceNeedsKnown: 'Harjoittelu onnistuu, kun kaikki lauseen sanat on tunnistettu.',
  practiceNeedsSure: 'Valyriaksi käännettyä lausetta voi harjoitella vasta, kun käännös on varma – muuten harjoittelisit ehkä väärää muotoa.',

  alternatives: 'Myös mahdollinen',
  notes: 'Huomioita',
  notesEnglish: 'englanniksi',
  note: noteStrings('fi'),

  // ---- input read as typed (spoken Finnish, typos, "you") ----
  didYouMean: 'Tarkoititko:',
  didYouMeanUse: 'Korjaa teksti näin',
  didYouMeanNote: 'Käännös tehtiin korjatusta tekstistä.',
  readAsLabel: 'Luettiin näin:',
  asTyped: 'Käännä sellaisenaan',
  asTypedNote: 'Teksti käännettiin juuri niin kuin kirjoitit sen.',
  readAgain: 'Salli korjaukset',
  you: 'Englannin ”you”',
  youSg: 'sinä',
  youPl: 'te',
  youSgTitle: 'Yksi henkilö (valyriaksi ao)',
  youPlTitle: 'Useampi henkilö (valyriaksi jeme)',

  // ---- Selitä ----
  explainTitle: 'Selitys sana sanalta',
  explainIntro: 'Miksi kukin sana on juuri tässä muodossa. Selitys perustuu sovelluksen tulkintaan lauseesta.',
  ex: {
    unknown: 'Sovellus ei tunnista tätä sanaa, joten sitä ei voi selittää.',
    foreign: 'Tälle sanalle ei löytynyt valyrian vastinetta.',
    governorPost: <W>(w: W): Parts<W> => ['Tässä genetiivin vaatii sen perässä oleva postpositio ', w, '.'],
    governorPre: <W>(w: W): Parts<W> => ['Tässä sijan vaatii sen edellä oleva prepositio ', w, '.'],
    subjectOf: <W>(v: W): Parts<W> => ['Se on verbin ', v, ' subjekti.'],
    objectOf: <W>(v: W): Parts<W> => ['Se on verbin ', v, ' objekti.'],
    agreeAdj: <W>(n: W): Parts<W> => ['Adjektiivi taipuu pääsanansa ', n, ' mukaan: sama sija ja luku.'],
    adjNoHead: 'Adjektiivi taipuu pääsanansa mukaan: sama sija ja luku.',
    verbEnding: (label: string) => `Verbin pääte kertoo persoonan ja luvun: ${label}.`,
    verbSubject: <W>(s: W): Parts<W> => ['Pääte sopii subjektiin ', s, '.'],
    verbDropped: 'Subjektia ei ole kirjoitettu: valyriassa pääte riittää kertomaan, kuka tekee.',
    nonfinite: (label: string) => `Verbin muoto: ${label}.`,
    other: (pos: string) => `Sanaluokka: ${pos}.`,
  },

  // ---- token sheet ----
  tokenTitle: (w: string) => `Sana: ${w}`,
  tokenOf: (i: number, n: number) => `${i}/${n}`,
  prevWord: 'Edellinen sana',
  nextWord: 'Seuraava sana',
  inSentence: 'Tässä lauseessa',
  baseForm: 'perusmuoto',
  paradigmRow: 'Taivutusrivi',
  paradigmTable: 'Taivutus',
  paradigmHit: 'tämä muoto',
  readings: 'Muut mahdolliset tulkinnat',
  readingsInfo:
    'Kääntäjä valitsi tulkinnan lauseyhteyden perusteella. Muut tulkinnat näkyvät tiedoksi: napauttamalla näet niiden sanakirjatiedot, mutta käännös ei muutu.',
  readingsInfoPick:
    'Kääntäjä valitsi tulkinnan lauseyhteyden perusteella. Napauttamalla tulkintaa näet sen taivutusrivin ja sanakirjatiedot. ”Käytä tätä” kääntää lauseen uudelleen sillä tulkinnalla.',
  useReading: 'Käytä tätä',
  pickReadingAria: (word: string, what: string) => `Käytä sanalle ${word} tulkintaa: ${what}`,
  chosenByYou: 'sinun valintasi',
  resetReading: 'Anna kääntäjän valita',
  readingsNone: 'Muita tulkintoja ei ole.',
  chosen: 'valittu',
  showReading: 'Näytä tämän tulkinnan tiedot',
  unknownTitle: 'Tunnistamaton sana',
  unknownBody: (w: string) =>
    `Sanaa ”${w}” ei löytynyt sanakirjasta perusmuotona eikä taivutettuna. Se voi olla kirjoitusvirhe, nimi tai sana, jota sanakirjassa ei vielä ole.`,
  foreignTitle: 'Ei valyriaksi',
  foreignBody: (w: string) =>
    `Sanalle ”${w}” ei löytynyt valyrian vastinetta. Sanakirjasta voi puuttua juuri tämä merkitys, tai sana on lauseessa muodossa, jota kääntäjä ei tunnista.`,
  openEntry: 'Avaa sanakirjassa',

  // ---- flag ----
  flag: 'Merkitse tarkistettavaksi',
  flagTitle: 'Käännös tarkistettavaksi',
  flagIntro:
    'Merkintä menee tarkistusjonoon. Kun korjaat käännöksen oikeaksi, se lisätään myös kääntäjän testilauseisiin, jotta sama virhe ei toistu huomaamatta.',
  flagNote: 'Mikä meni pieleen?',
  flagNoteSeed: (out: string) => `Kääntäjä antoi ”${out.replace(/[.!?]+$/, '')}”, mutta `,
  flagNotePlaceholder: 'esim. verbi on väärässä aikamuodossa',
  flagExpected: 'Oikea käännös',
  flagExpectedHint: 'Muokkaa kääntäjän tulos oikeaksi. Tyhjänä merkintä menee vain tarkistusjonoon.',
  flagExpectedPlaceholder: 'oikea käännös kokonaisuudessaan',
  flagSave: 'Tallenna merkintä',
  flagSaved: 'Merkitty tarkistettavaksi.',
  flagCaseQueued: 'Merkitty tarkistettavaksi. Oikea käännös lähtee kääntäjän testilauseisiin, kun yhteys palaa.',
  flagCaseSent: 'Merkitty tarkistettavaksi ja lisätty kääntäjän testilauseisiin.',
  flagDemo: 'Demotilassa merkintä jää tälle laitteelle eikä lähde testilauseisiin.',
  noteRequired: 'Kirjoita lyhyt huomautus ennen tallennusta.',

  // ---- practice (free practice → exercises) ----
  practiceFrom: 'Lause kääntäjästä',
  practiceFromNote: 'Harjoitukset on koottu lauseesta, jonka käänsit. Lauseella ei ole virallista käännöstä, joten sen merkitystä ei kysytä.',
  practiceNone: 'Tästä lauseesta ei saatu harjoituksia: siinä pitää olla ainakin kaksi sanaa tai yksi taipuva sana.',
  practiceBack: 'Takaisin kääntäjään',
  practiceContinue: 'Jatka tavallisiin harjoituksiin',

  // ---- phase 3: letter mode and tone (items 16, P4-17), borrowed names (17), flashcards (19) ----
  letterMode: 'Kirje',
  letterModeHint: 'Kirje käännetään rivi riviltä, ja kappaleet säilyvät. Puhuttelu ja lopetus käännetään kirjeen tapaan.',
  tone: 'Sävy',
  tones: { intimate: 'Tuttavallinen', formal: 'Muodollinen' } as Record<'intimate' | 'formal', string>,
  toneHint: {
    intimate: 'Hellittelynimet ja tavallinen käskymuoto (oppitunnit 41 ja 42).',
    formal: 'Arvonimi, ja pyyntö sanotaan toivomuksena: arvonimi vokatiivissa ja verbi infinitiivissä (oppitunnit 41 ja 42).',
  } as Record<'intimate' | 'formal', string>,
  titleLabel: 'Arvonimi',
  letterLines: 'Kirje riveittäin',
  lineKind: { opener: 'puhuttelu', closer: 'lopetus', signature: 'allekirjoitus' } as Record<'opener' | 'closer' | 'signature', string>,
  lineUnsure: 'epävarma rivi',
  wordByWord: 'Sana sanalta',
  namesHead: (n: number): string => (n === 1 ? 'Lainanimi' : 'Lainanimet'),
  namesIntro:
    'Nimet, joita sanakirjassa ei ole, taivutetaan lainasanojen tapaan (oppitunnit 20, 29 ja 41). Kaikki muodot tulevat taivutustaulukosta. Kirjoitusasu on ehdotus.',
  nameClass: (decl: string, gender: string, model: string) => `${decl}. deklinaatio, ${gender}, kuten ${model}`,
  nameSpelling: 'Kirjoitusasu',
  nameTag: 'lainanimi',
  nameCases: (name: string) => `Nimen ${name} taivutus yksikössä`,
  nameUnsure: 'Taivutusluokka on arvaus, koska nimen loppu ei sovi mihinkään luokkaan varmasti.',
  makeCard: 'Tee kortti',
  removeCard: 'Poista kortti',
  cardRemoved: 'Kortti on poistettu kertauksesta. Saat sen takaisin napilla ”Tee kortti”.',
  cardNeedsSure: 'Kortin voi tehdä, kun kaikki sanat on tunnistettu ja käännös on varma tai virallinen.',
  cardDone: 'Lause on nyt kertauskortti. Se tulee vastaan päivän kertauksessa.',

  // ---- phase 3 item 18: usage log (Asetukset, Ylläpito) ----
  usage: {
    title: 'Kääntäjän käyttöloki',
    adminIntro: 'Molempien tallentamat epävarmat käännökset, uusin ensin.',
    adminEmpty: 'Lokissa ei ole merkintöjä.',
    adminClear: 'Tyhjennä koko loki',
    adminCleared: 'Koko loki tyhjennettiin.',
    confidence: (pct: number) => `varmuus ${pct}\u00a0%`,
  },
};

export const translatorEn: typeof translatorFi = {
  title: 'Translator',

  direction: 'Direction',
  directions: {
    'hv-fi': 'Valyrian to Finnish',
    'hv-en': 'Valyrian to English',
    'fi-hv': 'Finnish to Valyrian',
    'en-hv': 'English to Valyrian',
  },
  writeIn: 'You write',
  translateTo: 'Translation',
  srcLang: { hv: 'Valyrian', fi: 'Finnish', en: 'English' },
  tgtLang: { hv: 'into Valyrian', fi: 'into Finnish', en: 'into English' },
  swapTo: (d) => `Swap: ${d}`,
  swapMoves: 'The translation becomes the text to translate, so you can translate it back.',

  inputLabel: {
    'hv-fi': 'Write in High Valyrian',
    'hv-en': 'Write in High Valyrian',
    'fi-hv': 'Write in Finnish',
    'en-hv': 'Write in English',
  },
  placeholder: {
    'hv-fi': 'e.g. Zaldrīzes buzdari iksos daor.',
    'hv-en': 'e.g. Zaldrīzes buzdari iksos daor.',
    'fi-hv': 'esim. Nainen näkee miehen.',
    'en-hv': 'e.g. The woman sees the man.',
  },
  translate: 'Translate',
  clear: 'Clear',
  keysHint: (mod) => `${mod} + Enter translates now, Esc clears.`,
  waitIndex: 'The translator needs the index of inflected forms, which is built once on this device. Please wait a moment.',
  indexFailed: 'The index of inflected forms could not be built, so the translator does not work on this device right now. Reload the page.',

  lists: 'Recent and saved',
  tabs: { recent: 'Recent', saved: 'Saved', show: 'From the shows' },
  recentEmpty: 'Your last 30 translations show here. They are kept on this device only.',
  savedEmpty: 'Save a translation to find it here. A saved sentence can also be practised.',
  clearRecent: 'Clear recent',
  remove: (s) => `Remove “${s}”`,
  tryThese: 'Try a line from the show',
  noFiExamples: 'The show lines have no Finnish translations yet, so there are no examples here. Write a sentence of your own.',

  result: 'Translation',
  srResult: (s) => `Translation: ${s}`,
  source: 'Source text',
  stack: 'Word by word: Valyrian, meaning, translation',
  rowHv: 'Valyrian',
  rowGloss: 'meaning',
  rowFree: { fi: 'in Finnish', en: 'in English' },
  tapHint: 'Tap a word to see its reading, its inflection and its dictionary entry.',
  unknownWord: 'unknown',
  foreignWord: 'not in Valyrian',
  tokenAria: (word, gloss, label) => [word, gloss, label].filter(Boolean).join(' – '),

  official: 'Official translation',
  officialWiki: 'Peterson’s wiki',
  officialPhrase: 'dictionary phrase',
  officialBelow: 'Below, the translator’s own rule-based translation, word by word.',
  oursMatch: 'Our translation',
  oursApproved: (name) => `approved by ${name}`,
  builtPeterson: 'Built on a sentence by Peterson',
  builtOurs: 'Built on our translation',
  builtFrame: 'The sentence it is built on',
  builtSwaps: 'Words swapped',
  builtHow:
    'The sentence structure and the word forms come from the original sentence. The swapped words are put in the same form by their own inflection tables. Check that the meaning still makes sense.',
  builtFiFrame: 'The sentence was found among the Finnish translations.',
  partialTitle: 'Partly from our translations',
  partialHow: 'The marked parts are translations you approved. The translator made the other parts by its rules.',
  partialFrom: 'approved',
  partialBuilt: 'built on an approved one',

  confidence: 'Confidence',
  levels: { high: 'Sure', medium: 'Fairly sure', low: 'Unsure' },
  reasons: {
    allKnown: () => 'every word was recognised',
    unknown: (n) => `${enN(n, 'word', 'words')} not recognised`,
    noFiGlosses: () => 'there are no Finnish meanings yet',
    noVerb: () => 'no verb was recognised',
    noFiniteVerb: () => 'the only verb is an infinitive',
    closest: () => 'some forms are the closest found',
    typo: () => 'a typo was corrected by a guess',
    endingOnly: () => 'some words were recognised by their ending only',
    fallbackGloss: () => 'some words have no Finnish meaning yet',
    unsureFi: () => 'some Finnish forms are unsure',
    approx: () => 'one case was translated approximately',
    names: (n) => (n === 1 ? 'one word was kept as a name' : `${n} words were kept as names`),
    draft: () => 'the Finnish meanings include unreviewed drafts',
    order: () => 'the word order was changed',
  },
  lowAdvice: 'Treat the result as a rough pointer and check the words in the dictionary.',

  unknownHead: (n) => (n === 1 ? 'Unrecognised word' : 'Unrecognised words'),
  foreignHead: (n) => (n === 1 ? 'Word with no Valyrian equivalent' : 'Words with no Valyrian equivalent'),
  suggestWord: 'Suggest a word',
  searchDict: 'Search the dictionary',

  save: 'Save',
  saved: 'Saved',
  explain: 'Explain',
  practice: 'Practise this sentence',
  practiceNeedsKnown: 'Practice works once every word of the sentence is recognised.',
  practiceNeedsSure: 'A sentence translated into Valyrian can be practised only when the translation is sure – otherwise you might practise a wrong form.',

  alternatives: 'Also possible',
  notes: 'Notes',
  notesEnglish: 'in English',
  note: noteStrings('en'),

  didYouMean: 'Did you mean:',
  didYouMeanUse: 'Correct the text',
  didYouMeanNote: 'The corrected text was translated.',
  readAsLabel: 'Read as:',
  asTyped: 'Translate as written',
  asTypedNote: 'The text was translated exactly as you wrote it.',
  readAgain: 'Allow corrections',
  you: 'English “you”',
  youSg: 'sinä',
  youPl: 'te',
  youSgTitle: 'One person (Valyrian ao)',
  youPlTitle: 'Several people (Valyrian jeme)',

  explainTitle: 'Word by word, explained',
  explainIntro: 'Why each word has the form it has. The explanation follows the app’s reading of the sentence.',
  ex: {
    unknown: 'The app does not recognise this word, so it cannot explain it.',
    foreign: 'No Valyrian equivalent was found for this word.',
    governorPost: (w) => ['Here the postposition ', w, ' after it requires the genitive.'],
    governorPre: (w) => ['Here the preposition ', w, ' before it requires this case.'],
    subjectOf: (v) => ['It is the subject of the verb ', v, '.'],
    objectOf: (v) => ['It is the object of the verb ', v, '.'],
    agreeAdj: (n) => ['The adjective agrees with its noun ', n, ': same case and number.'],
    adjNoHead: 'The adjective agrees with its noun: same case and number.',
    verbEnding: (label) => `The verb ending gives the person and number: ${label}.`,
    verbSubject: (s) => ['The ending agrees with the subject ', s, '.'],
    verbDropped: 'No subject is written: in Valyrian the ending alone says who does it.',
    nonfinite: (label) => `Verb form: ${label}.`,
    other: (pos) => `Word class: ${pos}.`,
  },

  tokenTitle: (w) => `Word: ${w}`,
  tokenOf: (i, n) => `${i}/${n}`,
  prevWord: 'Previous word',
  nextWord: 'Next word',
  inSentence: 'In this sentence',
  baseForm: 'base form',
  paradigmRow: 'Inflection row',
  paradigmTable: 'Inflection',
  paradigmHit: 'this form',
  readings: 'Other possible readings',
  readingsInfo:
    'The translator chose its reading from the context. The other readings are shown for information: tap one to see its dictionary entry; the translation does not change.',
  readingsInfoPick:
    'The translator chose its reading from the context. Tap a reading to see its inflection row and dictionary entry. “Use this” translates the sentence again with that reading.',
  useReading: 'Use this',
  pickReadingAria: (word: string, what: string) => `Use this reading for ${word}: ${what}`,
  chosenByYou: 'your choice',
  resetReading: 'Let the translator choose',
  readingsNone: 'There are no other readings.',
  chosen: 'chosen',
  showReading: 'Show this reading’s entry',
  unknownTitle: 'Unrecognised word',
  unknownBody: (w) =>
    `“${w}” was not found in the dictionary or among any inflected forms. It may be a typo, a name, or a word the dictionary does not have yet.`,
  foreignTitle: 'Not in Valyrian',
  foreignBody: (w) =>
    `No Valyrian equivalent was found for “${w}”. The dictionary may lack this meaning, or the word is in a form the translator does not recognise.`,
  openEntry: 'Open in the dictionary',

  flag: 'Flag for review',
  flagTitle: 'Flag the translation',
  flagIntro:
    'The flag goes to the review queue. When you correct the translation, it is also added to the translator’s test sentences so the same mistake is caught from now on.',
  flagNote: 'What went wrong?',
  flagNoteSeed: (out) => `The translator gave “${out.replace(/[.!?]+$/, '')}”, but `,
  flagNotePlaceholder: 'e.g. the verb is in the wrong tense',
  flagExpected: 'Correct translation',
  flagExpectedHint: 'Edit the translator’s result into the correct one. Left empty, the flag goes to the review queue only.',
  flagExpectedPlaceholder: 'the whole correct translation',
  flagSave: 'Save the flag',
  flagSaved: 'Flagged for review.',
  flagCaseQueued: 'Flagged for review. The correct translation goes to the test sentences when you are back online.',
  flagCaseSent: 'Flagged for review and added to the translator’s test sentences.',
  flagDemo: 'In demo mode the flag stays on this device and does not reach the test sentences.',
  noteRequired: 'Write a short note before saving.',

  practiceFrom: 'Sentence from the translator',
  practiceFromNote: 'These exercises are built from the sentence you translated. It has no official translation, so its meaning is not asked.',
  practiceNone: 'No exercises could be built from this sentence: it needs at least two words or one inflected word.',
  practiceBack: 'Back to the translator',
  practiceContinue: 'Go on to the usual exercises',

  letterMode: 'Letter',
  letterModeHint: 'A letter is translated line by line, keeping its paragraphs. The opening and the closing are translated as in letters.',
  tone: 'Tone',
  tones: { intimate: 'Intimate', formal: 'Formal' },
  toneHint: {
    intimate: 'Pet names and the plain imperative (lessons 41 and 42).',
    formal: 'A title, and a request said as a wish: the title in the vocative and the verb in the infinitive (lessons 41 and 42).',
  },
  titleLabel: 'Title',
  letterLines: 'The letter line by line',
  lineKind: { opener: 'opening', closer: 'closing', signature: 'signature' },
  lineUnsure: 'unsure line',
  wordByWord: 'Word by word',
  namesHead: (n) => (n === 1 ? 'Borrowed name' : 'Borrowed names'),
  namesIntro:
    'Names the dictionary does not have are inflected the way borrowings are (lessons 20, 29 and 41). Every form comes from the inflection table. The spelling is a suggestion.',
  nameClass: (decl, gender, model) => `declension ${decl}, ${gender}, like ${model}`,
  nameSpelling: 'Spelling',
  nameTag: 'borrowed name',
  nameCases: (name) => `${name} in the singular`,
  nameUnsure: 'The class is a guess: the end of the name fits no class for certain.',
  makeCard: 'Make a card',
  removeCard: 'Remove card',
  cardRemoved: 'The card was removed from your reviews. “Make a card” brings it back.',
  cardNeedsSure: 'A card can be made once every word is recognised and the translation is sure or official.',
  cardDone: 'The sentence is now a review card. It comes up in your daily reviews.',

  usage: {
    title: 'Translator usage log',
    adminIntro: 'Unsure translations kept by both of you, newest first.',
    adminEmpty: 'The log is empty.',
    adminClear: 'Clear the whole log',
    adminCleared: 'The whole log was cleared.',
    confidence: (pct) => `confidence ${pct}%`,
  },
};

// Messages.translator (i18n/area.ts: collected by area-registry.ts)
export const area = defineArea('translator', translatorFi, translatorEn);
declare module '../area' {
  interface AreaMessages extends Record<typeof area.key, typeof translatorFi> {}
}
